import {
  Button,
  Flex,
  Spacer,
  Box,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  DarkMode,
  Text,
  Tooltip,
  Link,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  IconButton,
  useColorModeValue,
  useBreakpointValue,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";

import React, { useState } from "react";

import { ColorModeSwitcher } from "../ColorModeSwitcher";
import { useNavigate } from "react-router-dom";

import eduviclogo from "../resources/Eduvictechlogo.png";

import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";

import { FaGraduationCap, FaBookOpen } from "react-icons/fa";

import { coachingCategories } from "./coachingCategories";


interface NavBarProps {
  isAuthenticated: boolean;
  onLogout: () => void;
  userInitials: string | null;
}


const NavBar: React.FC<NavBarProps> = ({
  isAuthenticated,
  onLogout,
  userInitials,
}) => {
  const navigate = useNavigate();

  const [hover, setHover] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const hoverBg = useColorModeValue("gray.100", "gray.800");
  const hoverText = useColorModeValue("black", "white");

  const drawerBorderColor = useColorModeValue(
    "gray.200",
    "gray.600"
  );

  const drawerTextColor = useColorModeValue(
    "gray.800",
    "gray.100"
  );

  const drawerSecondaryTextColor = useColorModeValue(
    "gray.600",
    "gray.300"
  );


  /*
   * Existing navigation behaviour retained.
   *
   * Important:
   * Category navigation is handled separately below because
   * React Router does not automatically scroll to hash targets
   * when navigating within an SPA.
   */
  const handleNavigation = (path: string) => {
    if (path.startsWith("#")) {
      const element = document.getElementById(
        path.slice(1)
      );

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      return;
    }

    if (path.startsWith("/technologies")) {
      window.open(
        path,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }

    navigate(path);
  };


  /*
   * Category navigation.
   *
   * We deliberately do NOT rely on:
   *
   *   navigate("/coaching#category-x")
   *
   * alone.
   *
   * The landing page may not yet have rendered the target
   * element when navigation occurs. Therefore:
   *
   * 1. Navigate to /coaching
   * 2. Wait for React to render the landing page
   * 3. Locate #category-{slug}
   * 4. Scroll to it
   */
  const handleCategoryNavigation = (
    categorySlug: string,
    closeDrawer = false
  ) => {
    const targetId = `category-${categorySlug}`;

    const scrollToCategory = () => {
      const element = document.getElementById(targetId);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };

    /*
     * If already on /coaching, there is no need to navigate.
     * Just scroll directly.
     */
    if (window.location.pathname === "/coaching") {
      scrollToCategory();

      if (closeDrawer) {
        onClose();
      }

      return;
    }

    /*
     * Navigate to the Coaching landing page first.
     */
    navigate("/coaching");

    /*
     * Allow React Router / React to render the landing page
     * before attempting to find the target element.
     */
    window.setTimeout(() => {
      scrollToCategory();
    }, 100);

    if (closeDrawer) {
      onClose();
    }
  };


  /*
   * Domain navigation.
   */
  const handleDomainNavigation = (
    domainSlug: string,
    closeDrawer = false
  ) => {
    navigate(`/coaching/domain/${domainSlug}`);

     window.scrollTo(0, 0);

    if (closeDrawer) {
      onClose();
    }
  };


  /*
   * Mobile navigation helpers.
   */
  const handleMobileNavigation = (path: string) => {
    handleNavigation(path);
    onClose();
  };


return (
  <>
<Flex
  as="nav"
  bg="purple.600"
  opacity="0.9"
  boxShadow="2xl"
  p={2}
  align="center"
  justify="space-between"
  position="fixed"
  top="0"
  width="full"
  zIndex="sticky"
  height="70px"
>

  {/* ============================================================
      LEFT SECTION
      Logo + Eduvic Learning Platform
      ============================================================ */}

  <Flex
    align="center"
    gap={{ base: 1, lg: 2, xl: 3 }}
    minW={0}
    flexShrink={1}
  >

    <Image
      src={eduviclogo}
      alt="Logo"
      boxSize={{ base: "68px", lg: "74px", xl: "80px" }}
      objectFit="contain"
      cursor={hover ? "pointer" : "default"}
      transition="0.3s"
      onClick={() =>
        handleNavigation("/coaching")
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      flexShrink={0}
    />

    {!isMobile && (
      <Text
        fontWeight="bold"
        color="gray.300"
        fontSize={{ base: "sm", lg: "md" }}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        Learning Platform
      </Text>
    )}

  </Flex>


  {/* ============================================================
      CENTRE NAVIGATION
      Flexible — remains centred while available space allows
      ============================================================ */}

  {!isMobile && (

    <Flex
      flex="1"
      minW={0}
      justify="center"
      align="center"
      gap={{ base: 0, lg: 1, xl: 2 }}
      overflow="hidden"
      mx={{ base: 1, lg: 2, xl: 3 }}
    >

      {/* ========================================================
          EXPLORE PROGRAMS
          ======================================================== */}

      <Menu>

        <DarkMode>

          <MenuButton
            as={Button}
            variant="ghost"
            color="white"
            fontSize={{ base: "sm", lg: "md" }}
            size={{ base: "md", lg: "lg" }}
            px={{ base: 2, lg: 3, xl: 4 }}
            whiteSpace="nowrap"
            _hover={{
              bg: "purple.400",
            }}
            _focus={{
              boxShadow: "none",
            }}
          >
            Explore Programs
            <ChevronDownIcon ml={1} />
          </MenuButton>

        </DarkMode>


        <MenuList
          maxH="75vh"
          overflowY="auto"
          minW="340px"
          py={2}
        >

          {coachingCategories.map((category) => (

            <Box
              key={category.slug}
              px={2}
              py={1}
            >

              {/* CATEGORY */}

              <MenuItem
                borderRadius="lg"
                py={3}
                _hover={{
                  bg: "gray.100",
                }}
                onClick={() =>
                  handleCategoryNavigation(
                    category.slug
                  )
                }
                _focus={{
                  boxShadow: "none",
                }}
              >

                <HStack spacing={3}>

                  <FaGraduationCap
                    color="#805AD5"
                  />

                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color="purple.700"
                  >
                    {category.name}
                  </Text>

                </HStack>

              </MenuItem>


              {/* DOMAINS */}

              <VStack
                align="stretch"
                spacing={1}
                mt={1}
                pl={6}
              >

                {category.domains.map(
                  (domain) => (

                    <MenuItem
                      key={domain.slug}
                      borderRadius="lg"
                      py={2}
                      _hover={{
                        bg: "gray.100",
                      }}
                      onClick={() =>
                        handleDomainNavigation(
                          domain.slug
                        )
                      }
                      _focus={{
                        boxShadow: "none",
                      }}
                    >

                      <HStack spacing={3}>

                        <FaBookOpen
                          size={13}
                          color="#4A5568"
                        />

                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.700"
                        >
                          {domain.subtitle}
                        </Text>

                      </HStack>

                    </MenuItem>

                  )
                )}

              </VStack>


              <Divider my={2} />

            </Box>

          ))}

        </MenuList>

      </Menu>


      {/* ========================================================
          FEES
          ======================================================== */}

      <Button
        variant="ghost"
        color="white"
        fontSize={{ base: "sm", lg: "md" }}
        size={{ base: "md", lg: "lg" }}
        px={{ base: 2, lg: 3, xl: 4 }}
        whiteSpace="nowrap"
        _hover={{
          bg: "purple.400",
        }}
        onClick={() =>
          handleNavigation("/coaching/fees")
        }
      >
        Fees
      </Button>


      {/* ========================================================
          CONTACT
          ======================================================== */}

      <Button
        variant="ghost"
        color="white"
        fontSize={{ base: "sm", lg: "md" }}
        size={{ base: "md", lg: "lg" }}
        px={{ base: 2, lg: 3, xl: 4 }}
        whiteSpace="nowrap"
        _hover={{
          bg: "purple.400",
        }}
        onClick={() =>
          handleNavigation("/coaching/contact")
        }
      >
        Contact Us
      </Button>


      {/* ========================================================
          PRACTICE PLATFORM
          ======================================================== */}

      <Tooltip
        label="Eduvic Practice Platform"
        hasArrow
      >

        <Button
          variant="ghost"
          color="white"
          fontSize={{ base: "sm", lg: "md" }}
          size={{ base: "md", lg: "lg" }}
          px={{ base: 2, lg: 3, xl: 4 }}
          whiteSpace="nowrap"
          _hover={{
            bg: "purple.400",
          }}
          onClick={() =>
            navigate("/practice")
          }
        >
          Eduvic Practice Platform
        </Button>

      </Tooltip>

    </Flex>

  )}


  {/* ============================================================
      RIGHT SECTION
      ============================================================ */}

  <Flex
    align="center"
    gap={{ base: 1, lg: 2, xl: 3 }}
    flexShrink={0}
  >

    {/* ==========================================================
        EXISTING COACHING CTA BUTTONS
        ========================================================== */}

    <Button
      as={Link}
      variant="outline"
      color="white"
      _hover={{
        bg: "purple.400",
      }}
      fontSize={{ base: "sm", lg: "md" }}
      px={{ base: 2, lg: 3 }}
      whiteSpace="nowrap"
      onClick={() => {}}
      href="/platform/evc/enrol-online"
    >
      Enrol
    </Button>


    <Button
      as={Link}
      variant="outline"
      color="white"
      _hover={{
        bg: "purple.400",
      }}
      fontSize={{ base: "sm", lg: "md" }}
      px={{ base: 2, lg: 3 }}
      whiteSpace="nowrap"
      href="/platform/evc/login"
    >
      Login
    </Button>


    {/* ==========================================================
        MOBILE MENU
        ========================================================== */}

    {isMobile && (

      <IconButton
        aria-label="Open Menu"
        icon={<HamburgerIcon />}
        variant="ghost"
        color="white"
        _hover={{
          bg: "purple.400",
        }}
        onClick={onOpen}
      />

    )}


    <ColorModeSwitcher
      justifySelf="flex-end"
      _hover={{
        bg: "purple.400",
      }}
    />

  </Flex>

</Flex>

          {/* ============================================================
          MOBILE DRAWER
          ============================================================ */}

      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
      >
        <DrawerOverlay />

        <DrawerContent>

          <DrawerHeader>
            <Flex
              justify="space-between"
              align="center"
            >

              <Text
                fontSize="lg"
                fontWeight="bold"
              >
                Eduvic Learning Platform
              </Text>

              <IconButton
                aria-label="Close Menu"
                icon={<CloseIcon />}
                onClick={onClose}
                variant="ghost"
              />

            </Flex>
          </DrawerHeader>


          <DrawerBody>

            <VStack
              align="stretch"
              spacing={2}
            >

              {/* ==================================================
                  EXPLORE PROGRAMS
                  ================================================== */}

              <Box>

                <Text
                  px={3}
                  pt={2}
                  pb={3}
                  fontSize="md"
                  fontWeight="bold"
                  color={drawerTextColor}
                >
                  Explore Programs
                </Text>


                {coachingCategories.map(
                  (category) => (
                    <Box
                      key={category.slug}
                    >

                      {/* CATEGORY */}

                      <Button
                        variant="ghost"
                        justifyContent="flex-start"
                        width="100%"
                        borderRadius="lg"
                        py={6}
                        onClick={() =>
                          handleCategoryNavigation(
                            category.slug,
                            true
                          )
                        }
                      >
                        <HStack spacing={3}>

                          <FaGraduationCap />

                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                          >
                            {category.name}
                          </Text>

                        </HStack>
                      </Button>


                      {/* DOMAINS */}

                      <VStack
                        align="stretch"
                        spacing={1}
                        mt={1}
                        pl={6}
                      >
                        {category.domains.map(
                          (domain) => (
                            <Button
                              key={domain.slug}
                              variant="ghost"
                              justifyContent="flex-start"
                              size="sm"
                              borderRadius="lg"
                              pl={4}
                              onClick={() =>
                                handleDomainNavigation(
                                  domain.slug,
                                  true
                                )
                              }
                            >
                              <HStack spacing={3}>

                                <FaBookOpen />

                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                >
                                  {domain.subtitle}
                                </Text>

                              </HStack>
                            </Button>
                          )
                        )}
                      </VStack>


                      <Divider my={2} />

                    </Box>
                  )
                )}

              </Box>


              {/* ==================================================
                  FEES
                  ================================================== */}

              <Button
                variant="ghost"
                justifyContent="flex-start"
                onClick={() =>
                  handleMobileNavigation(
                    "/coaching/fees"
                  )
                }
              >
                <HStack spacing={3}>
                  <Text>Fees</Text>
                </HStack>
              </Button>


              <Divider />


              {/* ==================================================
                  CONTACT
                  ================================================== */}

              <Button
                variant="ghost"
                justifyContent="flex-start"
                onClick={() =>
                  handleMobileNavigation(
                    "/coaching/contact"
                  )
                }
              >
                <HStack spacing={3}>
                  <Text>Contact Us</Text>
                </HStack>
              </Button>


              <Divider />


              {/* ==================================================
                  PRACTICE PLATFORM
                  ================================================== */}

              <Button
                variant="ghost"
                justifyContent="flex-start"
                onClick={() =>
                  window.open(
                    "/technologies/",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <HStack spacing={3}>

                  <Text>
                    Eduvic Practice Platform
                  </Text>

                  <ExternalLinkIcon />

                </HStack>
              </Button>


              <Divider />

            </VStack>

          </DrawerBody>

        </DrawerContent>
      </Drawer>
  </>
);
};


export default NavBar;